import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Minimal platform settings for public/unauthenticated use
export interface PublicPlatformSettings {
  registration_enabled: boolean;
  maintenance_mode: boolean;
  maintenance_message: string;
  platform_name: string;
  support_email: string;
}

const DEFAULT_SETTINGS: PublicPlatformSettings = {
  registration_enabled: true,
  maintenance_mode: false,
  maintenance_message: 'We are currently performing maintenance. Please check back soon.',
  platform_name: 'AtlasDraft',
  support_email: 'support@atlasdraft.app',
};

// Keys that are safe to expose publicly (non-sensitive)
const PUBLIC_KEYS = [
  'registration_enabled',
  'maintenance_mode',
  'maintenance_message',
  'platform_name',
  'support_email',
];

export function usePlatformSettingsPublic() {
  const [settings, setSettings] = useState<PublicPlatformSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Use the edge function which handles public access
      const { data, error } = await supabase.functions.invoke('get-platform-settings', {
        body: {},
      });

      if (error) {
        console.error('Error fetching public platform settings:', error);
        return;
      }

      // Extract only public settings
      const publicSettings = { ...DEFAULT_SETTINGS };
      if (data?.settings) {
        for (const key of PUBLIC_KEYS) {
          if (key in data.settings) {
            (publicSettings as any)[key] = data.settings[key].value;
          }
        }
      }

      setSettings(publicSettings);
    } catch (err) {
      console.error('Error in usePlatformSettingsPublic:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    isLoading,
    refetch: fetchSettings,
  };
}
